/**
 * De hooks uit `Utils/Hooks.tsx` van het origineel (`useWindowSize`, `useHelpRequired`),
 * plus twee die vervangen wat de schermen daar met de hand deden.
 */
import { useLayoutEffect, useMemo, useState } from 'react';
import { useToken } from '@chakra-ui/react';
import { useLocation, useParams } from 'react-router';
import { InGameEffect, Role, type Game, type Player } from '@ame/shared';

/** `[breedte, hoogte]` van het venster, bijgewerkt bij elke `resize`. */
export function useWindowSize(): [number, number] {
  const [maat, setMaat] = useState<[number, number]>([0, 0]);
  useLayoutEffect(() => {
    const bijwerken = () => setMaat([window.innerWidth, window.innerHeight]);
    window.addEventListener('resize', bijwerken);
    bijwerken();
    return () => window.removeEventListener('resize', bijwerken);
  }, []);
  return maat;
}

/**
 * Per rol: bij welke effecten bovenaan de wachtrij die rol iets moet doen en op welk
 * scherm (het pad onder `/:gameId/:userId`). Overgenomen uit het origineel.
 */
const HULP_PER_ROL: Record<Role, { url?: string; acties: readonly InGameEffect[] }> = {
  [Role.inventory]: {
    url: 'magazijn',
    acties: [
      InGameEffect.buySparepart,
      InGameEffect.chooseSparepart,
      InGameEffect.sellSparepart,
      InGameEffect.cleanStorage,
    ],
  },
  [Role.assetData]: { acties: [] },
  [Role.dataManagementSystem]: { url: '', acties: [InGameEffect.stakeholderCalling] },
  [Role.organisation]: {
    url: 'organisatie',
    acties: [
      InGameEffect.upgradeMechanic,
      InGameEffect.upgradeMechanic4to5,
      InGameEffect.newMechanic,
    ],
  },
};

/**
 * Of deze speler nu iets moet doen en op welk scherm: `[true, 'magazijn']`. Het eerste
 * effect in `game.effectQueue` bepaalt het; de speler moet een rol hebben die erbij hoort.
 * `Layout` speelt dan een geluid en "Terug naar Fabriek" knippert, tot de speler op dat scherm is.
 */
export function useHelpRequired(
  game: Game | undefined,
  player: Player | undefined,
): [boolean, string | undefined] {
  const effect = game?.effectQueue[0];
  // Als tekst, omdat elke nieuwe staat een nieuwe lijst geeft (zie `useSpel`).
  const rollen = player?.role.join('\n') ?? '';
  return useMemo(() => {
    if (!effect || !rollen) return [false, undefined];
    const rol = (rollen.split('\n') as Role[]).find((r) =>
      HULP_PER_ROL[r]?.acties.includes(effect),
    );
    return rol ? [true, HULP_PER_ROL[rol]?.url] : [false, undefined];
  }, [effect, rollen]);
}

/**
 * Het pad onder `/:gameId/:userId`, zoals `useParams()['*']` in het origineel: `''` op de
 * fabriek, `'magazijn'`, `'assets/oven'`. Buiten de spelroutes `''`.
 */
export function useSchermPad(): string {
  const { gameId, userId } = useParams();
  const { pathname } = useLocation();
  const basis = `/${gameId ?? ''}/${userId ?? ''}`;
  if (!gameId || !userId || !pathname.startsWith(basis)) return '';
  return pathname.slice(basis.length).replace(/^\/+|\/+$/g, '');
}

/**
 * Zet de achtergrond van de hele pagina, zoals de schermen van het origineel deden met
 * `document.documentElement.style.setProperty('--background-color', ...)`. `kleur` is een
 * kleurtoken (`'magazijn.l'`, `'ameBlue.200'`) of een CSS-kleur (`'#c4e3e8'`).
 *
 * Net als in het origineel blijft de kleur staan na het verlaten van het scherm; het
 * volgende scherm zet zijn eigen kleur.
 */
export function useAchtergrondkleur(kleur: string): void {
  const [waarde] = useToken('colors', kleur);
  // Voor het tekenen, zodat het scherm niet eerst in de oude kleur verschijnt.
  useLayoutEffect(() => {
    if (waarde) document.documentElement.style.setProperty('--background-color', waarde);
  }, [waarde]);
}
